import React, { useRef, useState, useEffect, ReactNode } from 'react';
import './ScallopedShowcase.css';

type ShapeType = 'circle' | 'diamond';

interface BadgeConfig {
    emoji: string;
    text: string[];
    badgeColor: string;
}

interface ScallopedShowcaseProps {
    className?: string;
    children?: ReactNode;
    badge?: BadgeConfig;
    shapeType?: ShapeType;
    backgroundColor?: string;
    circleColor?: string;
    badgeTextColor?: string;
}

const ScallopedShowcase: React.FC<ScallopedShowcaseProps> = ({
    className = '',
    children,
    badge = {
        emoji: '🌽',
        text: ['MADE WITH CORN', 'NOT OIL'],
        badgeColor: 'specialYellow'
    },
    shapeType = 'circle',
    backgroundColor = 'bg-green',
    circleColor = '#16a34a',
    badgeTextColor = 'text-black'
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [cardWidth, setCardWidth] = useState(0);
    const [cardHeight, setCardHeight] = useState(0);

    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                // Calculate number of circles on top/bottom based on width divided by 75
                const circlesPerRow = Math.round(width / 75 / 2) * 2;
                const circleSize = width / circlesPerRow;

                setCardWidth(width);
                setCardHeight(circleSize); // Store circleSize for calculations
            }
        };

        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(updateDimensions, 100);
        window.addEventListener('resize', updateDimensions);

        // Observe content changes
        const observer = new ResizeObserver(() => {
            setTimeout(updateDimensions, 0);
        });

        if (contentRef.current) {
            observer.observe(contentRef.current);
        }

        return () => {
            window.removeEventListener('resize', updateDimensions);
            observer.disconnect();
        };
    }, []);

    // Calculate number of circles on top/bottom based on width divided by 75
    // Round to nearest even number to ensure symmetry
    const circlesPerRow = Math.round(cardWidth / 75 / 2) * 2;
    const circleSize = cardWidth / circlesPerRow;
    const circleRadius = circleSize / 2;

    // Calculate final card height based on content
    const calculateFinalHeight = () => {
        if (contentRef.current) {
            const refHeight = contentRef.current.clientHeight;
            const circles = Math.ceil(refHeight / circleSize);
            const totalHeight = circles * circleSize;


            // Round up to nearest circleSize multiple (circleHeight in this case)
            return totalHeight;
        }
        // Base height if content not ready (4:1 ratio)
        return (cardWidth / circlesPerRow) * (circlesPerRow / 4);
    };

    const finalCardHeight = calculateFinalHeight();

    // Calculate number of circles on left/right based on final card height
    // Always round up and add 1 to guarantee full coverage (circles are half-in, half-out)
    const circlesPerSide = (finalCardHeight / circleSize) + 1;
    // const heightInCircles = Math.floor(ratio) + 1;
    // // Ensure odd number for vertical symmetry
    // const circlesPerSide = heightInCircles % 2 === 0 ? heightInCircles + 1 : heightInCircles;

    // console.log('Circle calc:', { finalCardHeight, circleSize, heightInCircles, circlesPerSide, cardWidth, ratio });

    // Generate circle positions - positioned so they're half inside, half outside
    // Account for extra width from left/right circles sticking out by circleRadius
    const topCircles = Array.from({ length: circlesPerRow }, (_, i) => ({
        id: `top-${i}`,
        x: circleRadius + (i + 0.5) * circleSize,
        y: 0, // Positioned at the top edge, so half sticks out above
    }));

    const bottomCircles = Array.from({ length: circlesPerRow }, (_, i) => ({
        id: `bottom-${i}`,
        x: circleRadius + (i + 0.5) * circleSize,
        y: finalCardHeight, // Positioned at the bottom edge, so half sticks out below
    }));

    // Position side circles to align with corners and rows
    // Distribute evenly from top to bottom using finalCardHeight
    const leftCircles = Array.from({ length: circlesPerSide }, (_, i) => ({
        id: `left-${i}`,
        x: 0, // Positioned at the left edge, so half sticks out left
        y: (i * finalCardHeight) / (circlesPerSide - 1 || 1),
    }));

    const rightCircles = Array.from({ length: circlesPerSide }, (_, i) => ({
        id: `right-${i}`,
        x: cardWidth, // Positioned at the right edge, so half sticks out right
        y: (i * finalCardHeight) / (circlesPerSide - 1 || 1),
    }));

    const allCircles = [...topCircles, ...bottomCircles, ...leftCircles, ...rightCircles];

    // Function to render shape based on type
    const renderShape = (circle: any) => {
        if (shapeType === 'diamond') {
            return (
                <rect
                    key={circle.id}
                    x={circle.x - circleRadius}
                    y={circle.y - circleRadius}
                    width={circleSize}
                    height={circleSize}
                    transform={`rotate(45 ${circle.x} ${circle.y})`}
                    rx={circleRadius / 10}
                    fill={circleColor}
                />
            );
        }
        // Default to circle
        return (
            <circle
                key={circle.id}
                cx={circle.x}
                cy={circle.y}
                r={circleRadius}
                fill={circleColor}
            />
        );
    };

    return (
        <div
            className={`relative mx-auto my-12 overflow-visible z-5 ${className}`}
            style={{
                width: `calc(87.5% - 75px)`,
                maxWidth: 'calc(1400px - 75px)'
            }}
            ref={containerRef}
        >
            {/* Decorative circle badge - OUTSIDE card to avoid clipping */}
            <div className={`absolute -top-16 -left-16 md:-left-0 w-[120px] h-[120px] rounded-full bg-${badge.badgeColor} flex items-center justify-center shadow-lg z-20 transform -rotate-12`}>
                <div className="relative w-full h-full flex flex-col items-center justify-center text-center">
                    <span className="text-4xl block mb-1">{badge.emoji}</span>
                    <div className={`flex flex-col text-xs font-black ${badgeTextColor} tracking-wider leading-tight`}>
                        {badge.text.map((line, idx) => (
                            <span key={idx}>{line}</span>
                        ))}
                    </div>
                </div>
            </div>

            <div
                className={`relative py-12 ${backgroundColor} text-center rounded-3xl shadow-lg overflow-visible`}
                ref={cardRef}
                style={{ height: `${finalCardHeight}px` }}
            >
                {/* Scalloped edges using shapes - Background layer */}
                {cardWidth > 0 && (
                    <svg
                        className="absolute inset-0 w-full h-full pointer-events-none z-0 overflow-visible"
                        viewBox={`0 0 ${cardWidth} ${finalCardHeight}`}
                        preserveAspectRatio="none"
                    >
                        {allCircles.map((circle) => renderShape(circle))}
                    </svg>
                )}

                {/* Main content - Content layer (no fixed height) */}
                <div className="relative z-10 flex flex-col items-center justify-center h-full w-full">
                    <div ref={contentRef} className="flex flex-col items-center">
                        {children}
                    </div>
                </div>

                {/* Decorative icons */}
                {/* <div className="absolute top-1/5 right-[5%] text-3xl opacity-80 z-4 animate-float" style={{ animationDelay: '0s' }}>♻️</div>
                <div className="absolute bottom-[15%] left-[5%] text-4xl opacity-80 z-4 animate-float" style={{ animationDelay: '1s' }}>🌿</div>
                <div className="absolute top-[60%] right-[8%] text-2xl opacity-80 z-4 animate-float" style={{ animationDelay: '2s' }}>🌍</div> */}
            </div>
        </div>
    );
};

export default ScallopedShowcase;
